import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdChevronLeft, MdChevronRight, MdDeleteOutline } from "react-icons/md";
import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";
import { toast } from "react-toastify";
import { brandPalette } from "../../../constants/brandPalette";
import { getStudentHomeworkFile } from "../../../modules/admin-modules/reports/Api";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const RENDER_SCALE = 1.5;
const STROKE_COLOR = "#C41E3A";
const STROKE_WIDTH = 2.5;

function getCanvasPoint(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function canvasHasDrawing(canvas) {
  if (!canvas) return false;
  const ctx = canvas.getContext("2d");
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0) return true;
  }
  return false;
}

function canvasToPngBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error("Could not export annotation layer."));
          return;
        }
        resolve(await blob.arrayBuffer());
      },
      "image/png"
    );
  });
}

async function fetchHomeworkPdfBytes(filename) {
  const response = await getStudentHomeworkFile(filename);
  return response.data;
}

async function defaultFetchPdfBytes(filename) {
  return fetchHomeworkPdfBytes(filename);
}

async function buildAnnotatedPdf(originalBytes, annotationCanvases) {
  const pdfDoc = await PDFDocument.load(originalBytes);
  const pages = pdfDoc.getPages();

  for (let i = 0; i < pages.length; i++) {
    // UI stores annotations with 1-based page numbers; pdf-lib pages are 0-based.
    const canvas = annotationCanvases[i + 1];
    if (!canvas || !canvasHasDrawing(canvas)) continue;

    const pngBytes = await canvasToPngBytes(canvas);
    const pngImage = await pdfDoc.embedPng(pngBytes);
    const page = pages[i];
    const { width, height } = page.getSize();
    page.drawImage(pngImage, { x: 0, y: 0, width, height });
  }

  return pdfDoc.save();
}

const HomeworkPdfAnnotator = ({
  isOpen,
  onClose,
  filename,
  onSave,
  fetchPdfBytes = defaultFetchPdfBytes,
  modalTitle = "Annotate homework PDF",
  defaultFileName = "homework.pdf",
  loadErrorHint = "Homework file endpoint not found. Add GET /api/admin/student_homeworks/file/{filename} on the server.",
}) => {
  const pdfCanvasRef = useRef(null);
  const annotationCanvasRef = useRef(null);
  const annotationCanvasesRef = useRef({});
  const isDrawingRef = useRef(false);
  const pdfBytesRef = useRef(null);
  const pdfDocRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });

  const persistCurrentAnnotations = useCallback(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas || !currentPage) return;
    const stored = document.createElement("canvas");
    stored.width = canvas.width;
    stored.height = canvas.height;
    stored.getContext("2d").drawImage(canvas, 0, 0);
    annotationCanvasesRef.current[currentPage] = stored;
  }, [currentPage]);

  const restoreAnnotations = useCallback((pageNumber) => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const stored = annotationCanvasesRef.current[pageNumber];
    if (stored) {
      ctx.drawImage(stored, 0, 0);
    }
  }, []);

  const renderPage = useCallback(async (pageNumber) => {
    const pdfDoc = pdfDocRef.current;
    const pdfCanvas = pdfCanvasRef.current;
    const annotationCanvas = annotationCanvasRef.current;
    if (!pdfDoc || !pdfCanvas || !annotationCanvas) return;

    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({ scale: RENDER_SCALE });
    const width = Math.floor(viewport.width);
    const height = Math.floor(viewport.height);

    pdfCanvas.width = width;
    pdfCanvas.height = height;
    annotationCanvas.width = width;
    annotationCanvas.height = height;
    setPageSize({ width, height });

    const ctx = pdfCanvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    restoreAnnotations(pageNumber);
  }, [restoreAnnotations]);

  useEffect(() => {
    if (!isOpen || !filename) return;

    let cancelled = false;
    annotationCanvasesRef.current = {};
    setCurrentPage(1);
    setPageCount(0);
    setLoading(true);

    const loadPdf = async () => {
      try {
        const bytes = await fetchPdfBytes(filename);
        if (cancelled) return;

        pdfBytesRef.current = bytes;
        const pdfDoc = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
        if (cancelled) return;

        pdfDocRef.current = pdfDoc;
        setPageCount(pdfDoc.numPages);
        setLoading(false);
      } catch (error) {
        if (!cancelled) {
          const status = error?.response?.status;
          const message =
            status === 404
              ? loadErrorHint
              : status === 401
                ? "Session expired. Please log in again."
                : error?.response?.data?.message ||
                  error?.message ||
                  "Failed to load PDF.";
          toast.error(message);
          setLoading(false);
        }
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [isOpen, filename, fetchPdfBytes, loadErrorHint]);

  useEffect(() => {
    if (!isOpen || loading || !pageCount || !currentPage) return;
    renderPage(currentPage);
  }, [isOpen, loading, pageCount, currentPage, renderPage]);

  const startDrawing = (event) => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    isDrawingRef.current = true;
    const point = getCanvasPoint(canvas, event.clientX, event.clientY);
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = STROKE_COLOR;
    ctx.lineWidth = STROKE_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const draw = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(canvas, event.clientX, event.clientY);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    persistCurrentAnnotations();
  };

  const clearCurrentPage = () => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    delete annotationCanvasesRef.current[currentPage];
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > pageCount) return;
    persistCurrentAnnotations();
    setCurrentPage(nextPage);
  };

  const handleSave = async () => {
    if (!pdfBytesRef.current) return;
    persistCurrentAnnotations();

    const hasAnyDrawing = Object.values(annotationCanvasesRef.current).some(
      (canvas) => canvasHasDrawing(canvas)
    );
    if (!hasAnyDrawing) {
      toast.info("Add at least one annotation before saving.");
      return;
    }

    setSaving(true);
    try {
      const annotatedBytes = await buildAnnotatedPdf(
        pdfBytesRef.current,
        annotationCanvasesRef.current
      );
      const blob = new Blob([annotatedBytes], { type: "application/pdf" });
      const file = new File([blob], filename || defaultFileName, {
        type: "application/pdf",
      });
      onSave?.(file);
      onClose?.();
      toast.success("Annotations saved. Submit correction to upload.");
    } catch (error) {
      toast.error(
        error?.message?.includes("Failed to fetch")
          ? "Could not merge annotations into the PDF. Try clearing and re-drawing on fewer pages."
          : error?.message || "Failed to save annotated PDF."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    annotationCanvasesRef.current = {};
    pdfBytesRef.current = null;
    pdfDocRef.current = null;
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="95vw" dir="ltr">
        <ModalHeader pb={2}>
          <Text fontWeight="900" color="var(--text-primary)">
            {modalTitle}
          </Text>
          <Text fontSize="sm" fontWeight="600" color="var(--text-muted)">
            Draw corrections on the submission. Annotations are merged into the PDF on save.
          </Text>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <Flex minH="320px" align="center" justify="center">
              <Spinner color={brandPalette.primary} size="lg" />
            </Flex>
          ) : pageCount === 0 ? (
            <Text color="var(--text-muted)">No PDF pages to display.</Text>
          ) : (
            <Flex direction="column" align="center" gap={4}>
              <HStack spacing={3}>
                <IconButton
                  aria-label="Previous page"
                  icon={<MdChevronLeft size={22} />}
                  onClick={() => goToPage(currentPage - 1)}
                  isDisabled={currentPage <= 1}
                />
                <Text fontWeight="800" minW="120px" textAlign="center">
                  Page {currentPage} / {pageCount}
                </Text>
                <IconButton
                  aria-label="Next page"
                  icon={<MdChevronRight size={22} />}
                  onClick={() => goToPage(currentPage + 1)}
                  isDisabled={currentPage >= pageCount}
                />
                <Button
                  size="sm"
                  leftIcon={<MdDeleteOutline />}
                  variant="outline"
                  onClick={clearCurrentPage}
                >
                  Clear page
                </Button>
              </HStack>

              <Box
                position="relative"
                border="1px solid"
                borderColor="var(--border-subtle)"
                borderRadius="md"
                overflow="auto"
                maxH="70vh"
                bg="var(--surface-muted)"
                style={{
                  width: pageSize.width ? `${pageSize.width}px` : "auto",
                  height: pageSize.height ? `${pageSize.height}px` : "auto",
                }}
              >
                <canvas
                  ref={pdfCanvasRef}
                  style={{ position: "absolute", left: 0, top: 0 }}
                />
                <canvas
                  ref={annotationCanvasRef}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    cursor: "crosshair",
                    touchAction: "none",
                  }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    startDrawing(touch);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    draw(touch);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    stopDrawing();
                  }}
                />
              </Box>
            </Flex>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            bg={brandPalette.primary}
            color="white"
            _hover={{ bg: brandPalette.accentDark }}
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading || pageCount === 0}
          >
            Save annotations
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default HomeworkPdfAnnotator;
